import { asyncTimeout } from '@Utils/helpers/restHelper';
import { SortOrder } from '@Utils/helpers/sortingHelpers';
import _cloneDeep from 'lodash/cloneDeep';
import { ITeam } from '@Data/teams/Interfaces';
import { IUser } from '@Data/users/Interfaces';
import { projectEndPoints } from '@Services/data/ApiHandler';
import store from '@Services/redux/Store';
import FoldersApi from '../folders/Api';
import { IProject, IProjectDocument, ViewProjectSortField } from './Interfaces';
import { ProjectActions } from './Reducer';

const PROJECT_DOCUMENTS_KEY = 'documents';

const Api = {
    getProjects: async (teamId: string, callback?: () => void) => {
        const response = await projectEndPoints.getProjects(teamId);
        if (response.isSuccess) {
            const projects: Array<IProject> =
                response.data.projectDetails.filter((p) => !p.archived_flag);
            store.dispatch(ProjectActions.updateProjects(projects));

            if (callback) {
                callback();
            }
            return projects;
        } else {
            return false;
        }
    },
    setProjectById: (projectId: number, callback?: () => void) => {
        FoldersApi.reset();
        store.dispatch(ProjectActions.setProjectById(projectId));

        if (callback) {
            callback();
        }
    },
    setProject: (project: IProject | null, callback?: () => void) => {
        FoldersApi.reset();
        store.dispatch(ProjectActions.setProject(project));

        if (callback) {
            callback();
        }
    },
    _createProjectRequestBody: (
        teamId: string,
        projectName: string,
        projectInfo: object,
        archivedFlag: boolean
    ): object => {
        const output = {
            team_fk: teamId,
            project_name: projectName,
            project_info: projectInfo,
            archived_flag: archivedFlag,
        };

        return output;
    },
    createProject: async (teamId: string, projectName: string) => {
        const body = {
            team_fk: teamId,
            project_name: projectName,
            project_info: {},
            archived_flag: false,
            users: [],
        };
        const response = await projectEndPoints.createProject(body);

        if (response.isSuccess) {
            return response.data;
        }
    },
    _getEditProjectBody: (project: IProject) => {
        return {
            project_id: project.project_id,
            team_id: project.team_fk,
        };
    },
    editProject: async (selectedProject: IProject, newProjectName: string) => {
        const body = {
            ...Api._getEditProjectBody(selectedProject),
            project_name: newProjectName,
        };

        const response = await projectEndPoints.editProject(body);

        if (response.isSuccess) {
            return response.data;
        }
    },
    deleteProject: async (selectedProject: IProject) => {
        const body = {
            ...Api._getEditProjectBody(selectedProject),
            archived_flag: true,
        };

        const response = await projectEndPoints.editProject(body);

        if (response.isSuccess) {
            return response.data;
        }
    },
    setSortOrder: (newOrder: SortOrder) => {
        store.dispatch(ProjectActions.setSortOrder(newOrder));
    },
    setSortByField: (newField: ViewProjectSortField) => {
        store.dispatch(ProjectActions.setSortByField(newField));
    },
    getAndSelectProjectById: async (
        teamId: string,
        projectId: number
    ): Promise<IProject | boolean> => {
        const projects = await Api.getProjects(teamId);
        if (!projects) return false;
        const selectedProject = projects.find(
            (p) => p.project_id === projectId
        );
        Api.setProject(selectedProject);
        return selectedProject;
    },
    loadProjectDocuments: (selectedProject: IProject) => {
        const info = selectedProject.project_info;

        const docsEntry = Object.entries(info).find(
            ([key, value]) => key === PROJECT_DOCUMENTS_KEY
        );

        const docs = docsEntry ? docsEntry[1] : [];

        store.dispatch(ProjectActions.setSelectedProjectDocuments(docs));
    },
    _updateProjectInfo: (
        project: IProject,
        key: string,
        value: any
    ): { project_info: object } => {
        const cloned = _cloneDeep(project.project_info);
        cloned[key] = value;
        return {
            project_info: cloned,
        };
    },
    _removeDocFromDocuments: (
        document: IProjectDocument,
        documents: IProjectDocument[]
    ) => {
        return documents.filter((d) => d.id !== document.id);
    },
    _updateProjectInfoCallback: async (
        selectedTeam: ITeam,
        selectedProject: IProject
    ) => {
        const proj = await Api.getAndSelectProjectById(
            selectedTeam.team_id,
            selectedProject.project_id
        );
        return Api.loadProjectDocuments(proj as IProject);
    },
    deleteProjectDocument: async (
        selectedProject: IProject,
        selectedTeam: ITeam,
        document: IProjectDocument,
        existingDocuments: IProjectDocument[]
    ) => {
        const docs = existingDocuments.filter((d) => d.id !== document.id);

        const info = Api._updateProjectInfo(
            selectedProject,
            PROJECT_DOCUMENTS_KEY,
            docs
        );

        const response = await projectEndPoints.updateProjectInfo(
            selectedProject.project_id,
            info
        );

        if (response.isSuccess) {
            return await Api._updateProjectInfoCallback(
                selectedTeam,
                selectedProject
            );
        }
    },
    editProjectDocument: async (
        selectedProject: IProject,
        selectedTeam: ITeam,
        existingDocument: IProjectDocument,
        existingDocuments: IProjectDocument[],
        name: string,
        url: string
    ) => {
        const docs = [...existingDocuments];
        const existingDocIndex = docs.findIndex(
            (d) => d.id === existingDocument.id
        );

        const newDoc = {
            name: name,
            url: url,
            id: existingDocument.id,
        };

        if (existingDocIndex > -1) {
            docs[existingDocIndex] = newDoc;
            const info = Api._updateProjectInfo(
                selectedProject,
                PROJECT_DOCUMENTS_KEY,
                docs
            );

            const response = await projectEndPoints.updateProjectInfo(
                selectedProject.project_id,
                info
            );

            if (response.isSuccess) {
                return await Api._updateProjectInfoCallback(
                    selectedTeam,
                    selectedProject
                );
            }
        }
    },
    createProjectDocument: async (
        selectedProject: IProject,
        selectedTeam: ITeam,
        existingDocuments: IProjectDocument[],
        name: string,
        url: string
    ) => {
        const newDoc: IProjectDocument = {
            name: name,
            url: url,
            id: crypto.randomUUID(),
        };
        const docs = [...existingDocuments, newDoc];

        const info = Api._updateProjectInfo(
            selectedProject,
            PROJECT_DOCUMENTS_KEY,
            docs
        );

        const response = await projectEndPoints.updateProjectInfo(
            selectedProject.project_id,
            info
        );

        if (response.isSuccess) {
            return await Api._updateProjectInfoCallback(
                selectedTeam,
                selectedProject
            );
        }
    },
};

export default Api;
